import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Users, UserPlus, UserCheck, ClipboardList, ShoppingCart, FileText, LogOut, Key, Menu, X, Search, ChevronDown, ChevronRight, Package, Phone, ArrowLeft, Brain, Archive, Target, TrendingUp, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { changePassword, resetPassword } from '@/api/api';
import { toast } from 'sonner';
import { useActionHistory } from '@/context/ActionHistoryContext';
import MobileBottomNav from '@/components/MobileBottomNav';
import PWAInstallPrompt from '@/components/PWAInstallPrompt';

const Layout = ({ user, onLogout }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { undoLastAction, hasActions, clearActions } = useActionHistory();
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Clear action history when navigating to a different page
  useEffect(() => {
    clearActions();
  }, [location.pathname, clearActions]);

  const handleBackClick = () => {
    const actionUndone = undoLastAction();
    if (!actionUndone) {
      // If no action to undo, go back in history
      navigate(-1);
    }
  };
  const [showSearchSubMenu, setShowSearchSubMenu] = useState(false);
  const [showReportsSubMenu, setShowReportsSubMenu] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [resetUsername, setResetUsername] = useState('');
  const [securityAnswer, setSecurityAnswer] = useState('');
  const [resetNewPassword, setResetNewPassword] = useState('');

  const navigation = [
    { name: 'Dashboard', path: '/', icon: LayoutDashboard },
    { name: 'Müşteri Adayları', path: '/prospects', icon: UserPlus },
    { name: 'Teklifler', path: '/offers', icon: FileText },
    { name: 'Müşteriler', path: '/customers', icon: Users },
  ];
  
  // Abonelik Fiyat Teklifi - admin ve abonelik rolleri için
  if (user?.role === 'admin' || user?.role === 'abonelik') {
    navigation.push({ name: 'Abonelik Fiyat Teklifi', path: '/subscription-offers', icon: RefreshCw });
    navigation.push({ name: 'Abonelik Yönetimi', path: '/subscription-management', icon: ClipboardList });
  }
  
  // Portföyüm menüsü - sadece temsilci ve abonelik rolü
  if (user?.role === 'temsilci' || user?.role === 'abonelik') {
    navigation.push({ name: 'Portföyüm', path: '/portfolio', icon: Users });
  }
  
  navigation.push({ name: 'Ziyaretler', path: '/visits', icon: ClipboardList });

  // Aramalarım menüsü - tüm roller için
  navigation.push({ name: 'Aramalarım', path: '/calls', icon: Phone });

  // Aramalar alt menüleri - admin için tüm 4 menü, abonelik için Genel Aramalar hariç
  const searchMenuItems = user?.role === 'admin' ? [
    { name: 'Genel Aramalar', path: '/searches/general' },
    { name: 'Abonelik Aramaları', path: '/searches/subscription' },
    { name: 'Portföy Aramaları', path: '/searches/portfolio' },
    { name: 'Hoşgeldin Aramaları', path: '/searches/welcome' },
  ] : user?.role === 'abonelik' ? [
    { name: 'Abonelik Aramaları', path: '/searches/subscription' },
    { name: 'Portföy Aramaları', path: '/searches/portfolio' },
    { name: 'Hoşgeldin Aramaları', path: '/searches/welcome' },
  ] : [];

  // Diğer menüler
  const otherNavigation = [
    { name: 'Satışlar', path: '/sales', icon: ShoppingCart },
    { 
      name: 'Raporlar', 
      icon: FileText,
      subItems: [
        { name: 'Temsilci Performans', path: '/reports' },
        { name: 'Müşteri Satış Analizi', path: '/customer-sales-report' },
        { name: 'Ürün Bazlı Raporlar', path: '/product-based-reports' },
        { name: 'Satış Adet Raporları', path: '/sales-quantity-reports' },
      ]
    },
  ];

  if (user?.role === 'admin' || user?.role === 'yönetici') {
    otherNavigation.push({ name: 'Temsilciler', path: '/users', icon: UserCheck });
    otherNavigation.push({ name: 'Ürünler (Admin)', path: '/products', icon: ShoppingCart });
    otherNavigation.push({ name: 'Temsilci Ürünleri', path: '/representative-products', icon: Package });
    // AI Analitiği devre dışı bırakıldı
  }
  
  if (user?.role === 'temsilci' || user?.role === 'abonelik') {
    otherNavigation.push({ name: 'Ürünler', path: '/representative-products', icon: Package });
  }
  
  // Hedefler menüsü - sadece admin
  if (user?.role === 'admin') {
    otherNavigation.push({ name: 'Hedefler', path: '/targets', icon: FileText });
  }

  // Toplam Gelir menüsü - sadece admin (EN AŞAĞIYA)
  if (user?.role === 'admin') {
    otherNavigation.push({ name: 'Toplam Gelir', path: '/total-revenue', icon: TrendingUp });
  }

  // Hedef Gerçekleştirme - tüm roller görebilir
  otherNavigation.push({ name: 'Hedef Gerçekleştirme', path: '/target-achievement', icon: Target });

  // Ürün Kütüphanesi - en altta, tüm roller için
  otherNavigation.push({ name: 'Ürün Kütüphanesi', path: '/product-library', icon: Archive });

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const isSearchActive = () => {
    return location.pathname.startsWith('/searches');
  };

  const handleChangePassword = async () => {
    try {
      await changePassword(currentPassword, newPassword);
      toast.success('Şifre başarıyla değiştirildi');
      setShowPasswordDialog(false);
      setCurrentPassword('');
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Şifre değiştirilemedi');
    }
  };

  const handleResetPassword = async () => {
    try {
      await resetPassword(resetUsername, securityAnswer, resetNewPassword);
      toast.success('Şifre başarıyla sıfırlandı');
      setShowPasswordDialog(false);
      setResetUsername('');
      setSecurityAnswer('');
      setResetNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Şifre sıfırlanamadı');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="progo-gradient shadow-lg sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <Button
                variant="ghost"
                size="sm"
                className="lg:hidden text-gray-800 hover:bg-gray-300"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                data-testid="mobile-menu-button"
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </Button>
              <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                <span className="text-2xl font-bold text-blue-600">P</span>
              </div>
              <h1 className="text-xl md:text-2xl font-bold text-gray-800">ProGo Mobil Sistemler</h1>
            </div>
            <div className="flex items-center space-x-2 md:space-x-4">
              <div className="hidden md:block text-gray-800 text-sm">
                <div className="font-medium">{user?.full_name}</div>
                <div className="text-gray-600 text-xs">
                  {user?.role === 'admin' ? 'Admin' : user?.role === 'abonelik' ? 'Abonelik' : 'Temsilci'}
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowPasswordDialog(true)}
                className="bg-white hover:bg-gray-100 text-gray-800 border-gray-300 hidden md:flex"
                data-testid="change-password-button"
              >
                <Key className="w-4 h-4 mr-2" />
                Şifre Değiştir
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowPasswordDialog(true)}
                className="bg-white hover:bg-gray-100 text-gray-800 border-gray-300 md:hidden"
                data-testid="change-password-button-mobile"
              >
                <Key className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={onLogout}
                className="bg-white hover:bg-gray-100 text-gray-800 border-gray-300"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Çıkış
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside 
          className={`fixed lg:relative top-16 lg:top-0 left-0 w-64 h-[calc(100vh-4rem)] bg-gradient-to-b from-white to-gray-50 shadow-xl transition-transform duration-300 z-50 ${
            isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
          }`} 
          style={{ fontFamily: 'Poppins, sans-serif' }}
        >
          <nav className="h-full overflow-y-auto p-4 space-y-1.5" style={{ scrollbarWidth: 'thin', scrollbarColor: '#3B82F6 #E5E7EB' }}>
            {navigation.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  data-testid={`nav-${item.name.toLowerCase()}`}
                  className={`group flex items-center space-x-3 px-4 py-3.5 rounded-xl transition-all duration-200 ${
                    active
                      ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg shadow-blue-200 scale-[1.02]'
                      : 'text-gray-700 hover:bg-gradient-to-r hover:from-blue-50 hover:to-cyan-50 hover:text-blue-900 hover:shadow-md hover:scale-[1.01]'
                  }`}
                >
                  <div className={`p-2 rounded-lg transition-all ${
                    active 
                      ? 'bg-white/20' 
                      : 'bg-blue-100 group-hover:bg-blue-200'
                  }`}>
                    <Icon className={`w-5 h-5 ${active ? 'text-white' : 'text-blue-600'}`} />
                  </div>
                  <span className="font-semibold text-sm">{item.name}</span>
                </Link>
              );
            })}

            {/* Aramalar Menu - For Admin and Abonelik */}
            {(user?.role === 'admin' || user?.role === 'abonelik') && (
              <div>
                <button
                  onClick={() => setShowSearchSubMenu(!showSearchSubMenu)}
                  className={`group w-full flex items-center justify-between px-4 py-3.5 rounded-xl transition-all duration-200 ${
                    isSearchActive()
                      ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg shadow-blue-200 scale-[1.02]'
                      : 'text-gray-700 hover:bg-gradient-to-r hover:from-blue-50 hover:to-cyan-50 hover:text-blue-900 hover:shadow-md hover:scale-[1.01]'
                  }`}
                  data-testid="nav-aramalar"
                >
                  <div className="flex items-center space-x-3">
                    <div className={`p-2 rounded-lg transition-all ${
                      isSearchActive() 
                        ? 'bg-white/20' 
                        : 'bg-blue-100 group-hover:bg-blue-200'
                    }`}>
                      <Search className={`w-5 h-5 ${isSearchActive() ? 'text-white' : 'text-blue-600'}`} />
                    </div>
                    <span className="font-semibold text-sm">Aramalar</span>
                  </div>
                  {showSearchSubMenu ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                </button>

                {showSearchSubMenu && (
                  <div className="ml-8 mt-2 space-y-1.5 border-l-2 border-blue-200 pl-4">
                    {searchMenuItems.map((subItem) => {
                      const active = isActive(subItem.path);
                      return (
                        <Link
                          key={subItem.path}
                          to={subItem.path}
                          onClick={() => setIsMobileMenuOpen(false)}
                          data-testid={`nav-${subItem.name.toLowerCase().replace(/\s+/g, '-')}`}
                          className={`flex items-center space-x-3 px-4 py-2.5 rounded-lg transition-all text-xs ${
                            active
                              ? 'bg-blue-100 text-blue-900 font-semibold shadow-sm'
                              : 'text-gray-600 hover:bg-blue-50 hover:text-blue-900 font-medium'
                          }`}
                        >
                          <span className="text-blue-400">•</span>
                          <span>{subItem.name}</span>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Other Navigation Items */}
            {otherNavigation.map((item) => {
              const Icon = item.icon;
              
              // If item has subItems, render dropdown
              if (item.subItems) {
                const isAnySubActive = item.subItems.some(sub => isActive(sub.path));
                return (
                  <div key={item.name}>
                    <button
                      onClick={() => setShowReportsSubMenu(!showReportsSubMenu)}
                      className={`w-full group flex items-center justify-between space-x-3 px-4 py-3.5 rounded-xl transition-all duration-200 ${
                        isAnySubActive
                          ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg shadow-blue-200'
                          : 'text-gray-700 hover:bg-gradient-to-r hover:from-blue-50 hover:to-cyan-50 hover:text-blue-900 hover:shadow-md'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <div className={`p-2 rounded-lg transition-all ${
                          isAnySubActive 
                            ? 'bg-white/20' 
                            : 'bg-blue-100 group-hover:bg-blue-200'
                        }`}>
                          <Icon className={`w-5 h-5 ${isAnySubActive ? 'text-white' : 'text-blue-600'}`} />
                        </div>
                        <span className="font-semibold text-sm">{item.name}</span>
                      </div>
                      {showReportsSubMenu ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </button>
                    {showReportsSubMenu && (
                      <div className="ml-8 mt-1 space-y-1">
                        {item.subItems.map((subItem) => {
                          const subActive = isActive(subItem.path);
                          return (
                            <Link
                              key={subItem.path}
                              to={subItem.path}
                              onClick={() => setIsMobileMenuOpen(false)}
                              className={`block px-4 py-2.5 rounded-lg text-xs transition-all ${
                                subActive
                                  ? 'bg-blue-100 text-blue-900 font-semibold'
                                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                              }`}
                            >
                              {subItem.name}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }
              
              // Regular menu item
              const active = isActive(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  data-testid={`nav-${item.name.toLowerCase()}`}
                  className={`group flex items-center space-x-3 px-4 py-3.5 rounded-xl transition-all duration-200 ${
                    active
                      ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg shadow-blue-200 scale-[1.02]'
                      : 'text-gray-700 hover:bg-gradient-to-r hover:from-blue-50 hover:to-cyan-50 hover:text-blue-900 hover:shadow-md hover:scale-[1.01]'
                  }`}
                >
                  <div className={`p-2 rounded-lg transition-all ${
                    active 
                      ? 'bg-white/20' 
                      : 'bg-blue-100 group-hover:bg-blue-200'
                  }`}>
                    <Icon className={`w-5 h-5 ${active ? 'text-white' : 'text-blue-600'}`} />
                  </div>
                  <span className="font-semibold text-sm">{item.name}</span>
                </Link>
              );
            })}
          </nav>
        </aside>

        {/* Mobile overlay */}
        {isMobileMenuOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-30 lg:hidden"
            onClick={() => setIsMobileMenuOpen(false)}
          />
        )}

        {/* Main Content - Mobilde bottom nav için padding */}
        <main className="flex-1 p-2 md:p-4 pb-20 md:pb-4">
          {/* Geri Butonu - Son işlemi geri al */}
          <div className="mb-3">
            <Button
              variant="default"
              size="default"
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium shadow-md min-h-11"
              onClick={handleBackClick}
              title={hasActions ? "Son işlemi geri al (filtre/sıralama)" : "Önceki sayfaya dön"}
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Geri
            </Button>
          </div>
          <div className="max-w-full mx-auto px-2">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav user={user} />
      
      {/* PWA Install Prompt */}
      <PWAInstallPrompt />

      {/* Password Change Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Şifre İşlemleri</DialogTitle>
          </DialogHeader>
          <Tabs defaultValue="change" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="change">Şifre Değiştir</TabsTrigger>
              <TabsTrigger value="reset">Şifre Sıfırla</TabsTrigger>
            </TabsList>
            
            <TabsContent value="change" className="space-y-4">
              <div>
                <Label>Mevcut Şifre</Label>
                <Input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  data-testid="current-password-input"
                />
              </div>
              <div>
                <Label>Yeni Şifre</Label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  data-testid="new-password-input"
                />
              </div>
              <Button
                onClick={handleChangePassword}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="confirm-change-password"
              >
                Şifreyi Değiştir
              </Button>
            </TabsContent>
            
            <TabsContent value="reset" className="space-y-4">
              <div>
                <Label>Kullanıcı Adı</Label>
                <Input
                  value={resetUsername}
                  onChange={(e) => setResetUsername(e.target.value)}
                  data-testid="reset-username-input"
                />
              </div>
              <div>
                <Label>Güvenlik Sorusu Cevabı</Label>
                <Input
                  type="password"
                  value={securityAnswer}
                  onChange={(e) => setSecurityAnswer(e.target.value)}
                  placeholder="Cevabınız"
                  data-testid="security-answer-input"
                />
                <p className="text-sm text-gray-500 mt-1">Doğum tarihi formatı: GGAAYYYY</p>
              </div>
              <div>
                <Label>Yeni Şifre</Label>
                <Input
                  type="password"
                  value={resetNewPassword}
                  onChange={(e) => setResetNewPassword(e.target.value)}
                  data-testid="reset-new-password-input"
                />
              </div>
              <Button
                onClick={handleResetPassword}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="confirm-reset-password"
              >
                Şifreyi Sıfırla
              </Button>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Layout;